/**
 * Who else is on this board, where their hand is, and what they are holding
 * (SRS D2, docs/06-UI-SPEC.md §15.1).
 *
 * Presence is never saved. It lives for as long as the connection does and disappears
 * with it, which is why it travels beside the board rather than inside it.
 *
 * Cursor positions are sent a few times a second rather than on every movement: the
 * board redraws at 60 frames a second, but a teacher in Pakistan and a student in the
 * USA are 250 ms apart, and sending every frame would spend that link on nothing. The
 * gaps are filled in by moving each cursor smoothly towards where it really is.
 */

import type { PresenceState, PresenterView } from '@opal/board-doc';
import type { Point, Role } from '@opal/shared';
import type { Awareness } from 'y-protocols/awareness';

/** Who this device is, on this board. */
export interface BoardIdentity {
  readonly userId: string;
  /** The student's own name. Teachers are always shown as "Teacher" (SRS D2). */
  readonly displayName: string;
  readonly role: Role;
  /**
   * A student's place in the class, given to them when they typed the room code.
   *
   * Presented when the board is opened so the server can check what they may do
   * (SRS D4). Null for a teacher, whose browser carries a signed-in session instead.
   */
  readonly participantId: string | null;
}

/** How often a moving cursor is sent to everyone else. */
export const PRESENCE_INTERVAL_MS = 50;

export interface Presence {
  /** This person's hand moved, in canvas coordinates. Null when it leaves the board. */
  moveTo(cursor: Point | null): void;
  /** They picked up a different tool. */
  setTool(toolId: string): void;
  /** SRS D6 — the page they are on, so a class following them turns it too. */
  setPage(pageId: string): void;
  /** SRS D6 — where this teacher is looking, or null when not presenting. */
  setView(view: PresenterView | null): void;
  /** Everyone except this device. */
  others(): readonly PresenceState[];
  /** Called whenever anybody else appears, moves, or leaves. */
  onChange(handler: () => void): () => void;
  destroy(): void;
}

interface PresenceInternals {
  readonly awareness: Awareness;
  readonly identity: BoardIdentity;
  readonly handlers: Set<() => void>;
  cursor: Point | null;
  toolId: string;
  pageId: string;
  view: PresenterView | null;
  /** Set while a movement is waiting for its turn to be sent. */
  timer: ReturnType<typeof setTimeout> | null;
  sentAt: number;
}

function localState(state: PresenceInternals, movedAt: number): PresenceState {
  return {
    userId: state.identity.userId,
    displayName: state.identity.displayName,
    role: state.identity.role,
    cursor: state.cursor,
    toolId: state.toolId,
    pageId: state.pageId,
    handRaised: false,
    view: state.view,
    lastMovedAt: movedAt,
  };
}

function publish(state: PresenceInternals): void {
  state.sentAt = Date.now();
  state.awareness.setLocalState(localState(state, state.sentAt));
}

/** Sends now if enough time has passed, otherwise as soon as it has. */
function schedule(state: PresenceInternals): void {
  if (state.timer !== null) return;
  const wait = state.sentAt + PRESENCE_INTERVAL_MS - Date.now();
  if (wait <= 0) {
    publish(state);
    return;
  }
  state.timer = setTimeout(() => {
    state.timer = null;
    publish(state);
  }, wait);
}

/** Everyone whose state has arrived, minus this device's own. */
function readOthers(awareness: Awareness): readonly PresenceState[] {
  const others: PresenceState[] = [];
  for (const [clientId, value] of awareness.getStates()) {
    if (clientId === awareness.clientID) continue;
    const state = value as PresenceState | null;
    if (state?.userId) others.push(state);
  }
  return others;
}

/** What this person tells everybody else about themselves, and how often. */
function announcements(
  state: PresenceInternals,
): Pick<Presence, 'moveTo' | 'setTool' | 'setPage' | 'setView'> {
  return {
    moveTo(cursor) {
      state.cursor = cursor;
      // A hand leaving the board is told immediately: a cursor left behind on an
      // empty screen is worse than a slightly late one.
      if (cursor === null) publish(state);
      else schedule(state);
    },
    setTool(toolId) {
      if (state.toolId === toolId) return;
      state.toolId = toolId;
      publish(state);
    },
    setPage(pageId) {
      if (state.pageId === pageId) return;
      state.pageId = pageId;
      // Told at once, not on the throttle: a class following the teacher should turn
      // the page with them, not a fifth of a second behind them.
      publish(state);
    },
    setView(view) {
      state.view = view;
      // Panning and zooming move as fast as a cursor does, so this rides the same
      // throttle rather than sending a message per frame. Stopping is immediate.
      if (view === null) publish(state);
      else schedule(state);
    },
  };
}

/** Starts announcing this person and listening for everybody else. */
export function createPresence(awareness: Awareness, identity: BoardIdentity): Presence {
  const state: PresenceInternals = {
    awareness,
    identity,
    handlers: new Set<() => void>(),
    cursor: null,
    toolId: 'pen',
    pageId: '',
    view: null,
    timer: null,
    sentAt: 0,
  };

  const onRemoteChange = (): void => {
    for (const handler of state.handlers) handler();
  };
  awareness.on('change', onRemoteChange);
  publish(state);

  return {
    ...announcements(state),
    others: () => readOthers(awareness),
    onChange(handler) {
      state.handlers.add(handler);
      return () => {
        state.handlers.delete(handler);
      };
    },
    destroy() {
      if (state.timer !== null) clearTimeout(state.timer);
      state.timer = null;
      state.handlers.clear();
      awareness.off('change', onRemoteChange);
      awareness.setLocalState(null);
    },
  };
}
